import { useCallback, memo } from "react";
import { Button, Typography, Checkbox, MenuItem, Menu, MenuList, Box, Divider, useTheme } from "@mui/material";
import { useMainContext } from "../../../../../../context/useMainContext";
import { updateUrlParams } from "../../../../../../utils/urlParams";
import { statusOptions } from "../../../../DataGridTable/FieldTypes/StatusCell";

const QuickStatus = ({ statusAnchorEl, setStatusAnchorEl, handleCloseDots }) => {
  const { setShowOnlyApproved, filters, setFilters, isInitialLoad, filtersLogic, setFiltersLogic } = useMainContext();
  const theme = useTheme();

  const openStatusPopover = Boolean(statusAnchorEl);

  // Get all selected status values from filters
  const selectedStatusValues = filters.filter((filter) => filter.column === "status").map((filter) => filter.value);

  const handleStatusToggle = useCallback(
    (statusValue, statusOptions) => {
      setShowOnlyApproved(false);

      setFilters((prevFilters) => {
        const isCurrentlySelected = selectedStatusValues.includes(statusValue);
        let updatedFilters;

        if (isCurrentlySelected) {
          // Remove this specific status filter
          updatedFilters = prevFilters.filter((f) => !(f.column === "status" && f.value === statusValue));
        } else {
          // Add this status filter while keeping existing ones
          const selectedStatus = statusOptions.find((status) => status.value === statusValue);
          const newFilter = {
            id: Date.now() + Math.random(),
            column: "status",
            operator: "equals",
            value: statusValue,
            label: selectedStatus?.label || "",
            columnLabel: "Status",
          };
          updatedFilters = [...prevFilters, newFilter];
        }

        // Check status filters count and switch logic if needed
        const statusFiltersCount = updatedFilters.filter((f) => f.column === "status").length;
        let newLogic = filtersLogic;

        if (statusFiltersCount >= 2 && filtersLogic !== "or") {
          newLogic = "or";
          setFiltersLogic("or");
        } else if (statusFiltersCount < 2 && filtersLogic === "or") {
          newLogic = "and";
          setFiltersLogic("and");
        }

        // Update URL params if needed
        if (!isInitialLoad && newLogic !== filtersLogic) {
          updateUrlParams({ filters: updatedFilters, logic: newLogic });
        }

        return updatedFilters;
      });
    },
    [setShowOnlyApproved, setFilters, selectedStatusValues, filtersLogic, setFiltersLogic, isInitialLoad]
  );

  const handleClearStatus = useCallback(() => {
    setShowOnlyApproved(false);
    setFilters((prevFilters) => {
      const updatedFilters = prevFilters.filter((f) => f.column !== "status");
      // Reset logic to AND when clearing all status filters
      setFiltersLogic("and");
      if (!isInitialLoad) {
        updateUrlParams({ filters: updatedFilters, logic: "and" });
      }
      return updatedFilters;
    });
    setStatusAnchorEl(null);
    handleCloseDots();
  }, [setShowOnlyApproved, setFilters, setFiltersLogic, isInitialLoad, setStatusAnchorEl, handleCloseDots]);

  const handleClose = () => {
    setStatusAnchorEl(null);
    handleCloseDots();
  };

  return (
    <Menu
      open={openStatusPopover}
      anchorEl={statusAnchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
          },
        },
      }}
    >
      <MenuList
        sx={{
          overflow: "auto",
          maxHeight: `calc(${theme.components?.MuiMenu?.styleOverrides?.paper?.maxHeight || '40vh'} - 60px)`,
        }}
      >
        {statusOptions?.map((status) => (
          <MenuItem
            key={status.value}
            onClick={() => handleStatusToggle(status.value, statusOptions)}
            sx={{
              px: 1,
            }}
          >
            <Checkbox checked={selectedStatusValues.includes(status.value)} size="small" />
            <Typography>{status.label}</Typography>
          </MenuItem>
        ))}
      </MenuList>
      <Divider />
      <Box sx={{ p: 1 }}>
        <Button fullWidth variant="outlined" onClick={handleClearStatus} disabled={selectedStatusValues.length === 0}>
          Clear Status
        </Button>
      </Box>
    </Menu>
  );
};

export default memo(QuickStatus);
